import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";

import { AppLoader } from "~/components/_layout/AppLoader";
import { buildLDUserContext, InitLDClient, initLDClient } from "~/services/launchdarkly.service";
import { isFlagForcedOnLocally } from "~/shared/constants/feature-flag.constants";

import { useOrg } from "./orgProvider";
import { useUser } from "./userProvider";

type FeatureFlagContext = {
  getFeatureFlag: (featureFlag: string, defaultValue?: boolean) => boolean;
  getFeatureFlagString: (featureFlag: string, defaultValue?: string) => string;
};

type FeatureFlags = Record<string, boolean | string>;
type UnsubscribeFunc = () => void;
type FlagChangeValueResult = {
  current: boolean | string;
  previous: boolean | string;
};
type FlagChange = Record<string, FlagChangeValueResult>;

const FeatureFlagContext = createContext<FeatureFlagContext | undefined>(undefined);

export const FeatureFlagProvider = (props: PropsWithChildren): JSX.Element => {
  const { userId, userEmail } = useUser();
  const { orgId } = useOrg();
  const [ldClient, setLDClient] = useState<InitLDClient>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});

  useEffect(() => {
    let unsubscribe: UnsubscribeFunc | undefined;

    const init = async () => {
      const client = await initLDClient(userId, orgId);
      setLDClient(client);

      const initialFlags = client.allFlags();
      setFeatureFlags(initialFlags);

      const handleFlagChange = (flagChanges: FlagChange) => {
        setFeatureFlags(currentFeatureFlags => {
          const updatedFeatureFlags = { ...currentFeatureFlags };

          Object.keys(flagChanges).forEach(x => {
            updatedFeatureFlags[x] = flagChanges[x]?.current ?? false;
          });

          return updatedFeatureFlags;
        });
      };
      client.on("change", handleFlagChange);
      unsubscribe = () => client.off("change", handleFlagChange);

      setIsLoading(false);
    };

    init();

    return () => {
      if (!!unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId, orgId]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    identifyUser(userId, userEmail, orgId);
  }, [userId, userEmail, orgId, isLoading]);

  const identifyUser = async (userId: string, userEmail: string | null, orgId: string | null): Promise<void> => {
    if (!ldClient) {
      return;
    }

    const flags = await ldClient.identify(buildLDUserContext({ userId, userEmail, orgId }));
    setFeatureFlags(flags);
  };

  if (isLoading) {
    return <AppLoader label="Loading ShelfCycle Features" isLoading={isLoading} />;
  }

  const getFeatureFlag = (flagKey: string, defaultValue = false): boolean => {
    if (isFlagForcedOnLocally(flagKey)) {
      return true;
    }
    if (featureFlags.hasOwnProperty(flagKey)) {
      const value = featureFlags[flagKey];
      return typeof value === "boolean" ? value : defaultValue;
    }
    return defaultValue;
  };

  const getFeatureFlagString = (flagKey: string, defaultValue = ""): string => {
    if (featureFlags.hasOwnProperty(flagKey)) {
      const value = featureFlags[flagKey];
      return typeof value === "string" ? value : defaultValue;
    }
    return defaultValue;
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        getFeatureFlag,
        getFeatureFlagString,
      }}
    >
      {props.children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
};
